import { Fragment, useEffect, useRef, forwardRef, useImperativeHandle, MutableRefObject } from 'react';
import { FAIcon } from '../Icons/Icons';
import styles from './Menu.module.scss';
import { MenuProps, MenuItem } from './types/propTypes';

/**
 * @description Menu component provides a basic menu with list items.
 * The width of menu is set based on its text content and icon (if any).
 * To change the default width or menu position, pass a "className" and customize.
 * Props ->
 * menuItems: Array of list item objects.
 * onClose: callback to call when click outside the Menu.
 * className: prop to apply custom styling.
 */
const Menu = forwardRef<HTMLDivElement, MenuProps>(({ menuItems, className, onClose }: MenuProps, ref) => {
  const menuRef = useRef() as MutableRefObject<HTMLDivElement>;
  useImperativeHandle(ref, () => menuRef.current);
  useEffect(() => {
    const checkIfClickedOutside = (e: MouseEvent | KeyboardEvent) => {
      !menuRef.current?.contains(e.target as HTMLElement) && onClose();
    };
    document.addEventListener('mousedown', checkIfClickedOutside);
    document.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        checkIfClickedOutside(e);
      }
    });
    return () => {
      document.removeEventListener('mousedown', checkIfClickedOutside);
      document.removeEventListener('keydown', checkIfClickedOutside);
    };
  }, []);
  return (
    <div className={`${styles['menu-container']} ${className || ''}`} ref={menuRef}>
      <ul>
        {menuItems.map(({ disabled, id, icon, iconProps, onClick, title }, index) => (
          <Fragment key={id}>
            {index > 0 && <hr className={styles.divider} />}
            <li
              role="presentation"
              className={disabled ? styles.disabled : ''}
              onClick={(e) => {
                e.stopPropagation();
                !disabled && onClick(e);
              }}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (!disabled && e.key === 'Enter') {
                  onClick(e);
                }
              }}
              data-testid="menu-item-testid"
            >
              <span className={styles.title}>{title}</span>
              <FAIcon iconName={icon} className={styles.icon} {...iconProps} />
            </li>
          </Fragment>
        ))}
      </ul>
    </div>
  );
});
Menu.displayName = 'Menu';
export default Menu;
export type { MenuProps, MenuItem };
